import { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { scheduleMultiplierCellStyle } from '@/utils/heatmapColors';
import type { HourSlotMap } from '@/utils/hourlyBudgetHourSlots';
import { IST_HOUR_OPTIONS } from '@/utils/hourlyBudgetSchedule';
import { WEEKDAYS, type DayAmounts, type WeekdayIndex } from '@/utils/hourlyBudgetWeek';
import { formatSmartBudgetInr } from '@/utils/smartCampaignBudget';
import { cn } from '@/utils/cn';
import type { SmartBudgetWeekday } from '@/types/smartBudget';

type SmartBudgetWeekGridProps = {
  slotsByDay: Record<WeekdayIndex, HourSlotMap>;
  amountsByDay: Record<WeekdayIndex, DayAmounts>;
  weekdays: SmartBudgetWeekday[];
  selectedDay?: number;
};

function heatmapHourLabel(label: string): string {
  return label.replace(':00', '');
}

function formatAmountCell(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '—';
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return '—';
  return formatSmartBudgetInr(parsed);
}

function hourMetrics(weekdays: SmartBudgetWeekday[], day: WeekdayIndex, hour: number) {
  return weekdays.find((row) => row.day_of_week === day)?.hours.find((slot) => slot.hour === hour);
}

function cellUnlockAmount(
  selected: boolean,
  metrics: ReturnType<typeof hourMetrics>,
  hourlyRaw: string,
  multiplierRaw: string
): number | null {
  if (!selected) return null;
  if (metrics && Number.isFinite(metrics.amount) && metrics.amount > 0) return metrics.amount;
  const hourly = Number(hourlyRaw);
  const multiplier = Number(multiplierRaw);
  if (!Number.isFinite(hourly) || hourly <= 0 || !Number.isFinite(multiplier) || multiplier <= 0) {
    return null;
  }
  return Math.round(hourly * multiplier * 100) / 100;
}

function amountCellStyle(amount: number | null, max: number) {
  if (amount == null || amount <= 0 || max <= 0) return scheduleMultiplierCellStyle(null);
  return scheduleMultiplierCellStyle(1 + (amount / max) * 4);
}

function cellTitle(
  dayLabel: string,
  hourLabel: string,
  amount: number | null,
  metrics: ReturnType<typeof hourMetrics>
): string {
  const parts = [`${dayLabel} ${hourLabel}`];
  if (amount != null) parts.push(formatSmartBudgetInr(amount));
  if (metrics) {
    parts.push(`sales ${formatSmartBudgetInr(metrics.sales)}`);
    parts.push(`ROAS ${metrics.roas.toFixed(2)}`);
  } else if (amount == null) {
    parts.push('off');
  }
  return parts.join(' · ');
}

export function SmartBudgetWeekGrid({
  slotsByDay,
  amountsByDay,
  weekdays,
  selectedDay,
}: SmartBudgetWeekGridProps) {
  const maxUnlock = useMemo(() => {
    let max = 0;
    for (const day of WEEKDAYS) {
      const hourly = amountsByDay[day.index]?.hourly ?? '';
      const slots = slotsByDay[day.index] ?? {};
      for (const opt of IST_HOUR_OPTIONS) {
        const selected = slots[opt.hour] != null;
        const amount = cellUnlockAmount(
          selected,
          hourMetrics(weekdays, day.index, opt.hour),
          hourly,
          slots[opt.hour] ?? '1'
        );
        if (amount != null && amount > max) max = amount;
      }
    }
    return max;
  }, [amountsByDay, slotsByDay, weekdays]);

  return (
    <div>
      <p className="mb-2 inline-flex items-center gap-1.5 text-xs text-foreground-muted">
        <Clock className="h-3.5 w-3.5 shrink-0" />
        All times are in IST (India Standard Time). Hours run down; each column is one weekday.
      </p>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full border-collapse text-xs">
          <thead className="bg-page text-foreground-muted">
            <tr>
              <th className="sticky left-0 z-20 min-w-[74px] border-b border-r border-border bg-page px-2 py-2 text-left font-medium">
                Hour
              </th>
              {WEEKDAYS.map((day) => (
                <th
                  key={day.index}
                  className={cn(
                    'border-b border-border px-2 py-2 text-center font-medium',
                    selectedDay === day.index && 'bg-brand-subtle text-foreground'
                  )}
                >
                  {day.short}
                </th>
              ))}
            </tr>
            <tr>
              <th className="sticky left-0 z-20 border-b border-r border-border bg-page px-2 py-1.5 text-left font-medium">
                Base
              </th>
              {WEEKDAYS.map((day) => {
                const amounts = amountsByDay[day.index] ?? { hourly: '', base: '' };
                return (
                  <td
                    key={day.index}
                    aria-label={`${day.long} base budget`}
                    className={cn(
                      'border-b border-border px-1 py-1.5 text-center tabular-nums text-foreground',
                      selectedDay === day.index && 'bg-brand-subtle'
                    )}
                  >
                    {formatAmountCell(amounts.base)}
                  </td>
                );
              })}
            </tr>
            <tr>
              <th className="sticky left-0 z-20 border-b border-r border-border bg-page px-2 py-1.5 text-left font-medium">
                Hourly
              </th>
              {WEEKDAYS.map((day) => {
                const amounts = amountsByDay[day.index] ?? { hourly: '', base: '' };
                return (
                  <td
                    key={day.index}
                    aria-label={`${day.long} hourly budget`}
                    className={cn(
                      'border-b border-border px-1 py-1.5 text-center tabular-nums text-foreground',
                      selectedDay === day.index && 'bg-brand-subtle'
                    )}
                  >
                    {formatAmountCell(amounts.hourly)}
                  </td>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {IST_HOUR_OPTIONS.map((opt) => (
              <tr key={opt.hour} className="border-b border-border last:border-b-0">
                <td className="sticky left-0 z-10 whitespace-nowrap border-r border-border bg-surface px-2 py-1.5 text-foreground-muted">
                  {heatmapHourLabel(opt.label)}
                </td>
                {WEEKDAYS.map((day) => {
                  const slots = slotsByDay[day.index] ?? {};
                  const selected = slots[opt.hour] != null;
                  const metrics = hourMetrics(weekdays, day.index, opt.hour);
                  const amount = cellUnlockAmount(
                    selected,
                    metrics,
                    amountsByDay[day.index]?.hourly ?? '',
                    slots[opt.hour] ?? '1'
                  );
                  const style = amountCellStyle(amount, maxUnlock);
                  return (
                    <td
                      key={day.index}
                      className="border-border p-0 text-center font-medium"
                      style={style}
                      title={cellTitle(day.short, opt.label, amount, metrics)}
                    >
                      <span
                        className="flex h-8 w-full min-w-[3.5rem] items-center justify-center px-1 text-[11px] font-semibold tabular-nums"
                        style={{ color: style.color }}
                      >
                        {amount != null ? formatSmartBudgetInr(amount) : ''}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-foreground-muted">
        <span>Unlock</span>
        <span className="inline-flex items-center gap-1">
          <span
            className="inline-block h-3 w-5 rounded-sm border border-border"
            style={amountCellStyle(1, 4)}
          />
          Lower
        </span>
        <span className="inline-flex items-center gap-1">
          <span
            className="inline-block h-3 w-5 rounded-sm border border-border"
            style={amountCellStyle(4, 4)}
          />
          Higher
        </span>
      </div>
    </div>
  );
}
